/**
 * Free Shipping Progress Bar
 *
 * Visual progress indicator showing how close the user is to the free shipping
 * threshold. Three visual states:
 *   - hidden:   threshold not configured (isEnabled=false)
 *   - progress: cart is below threshold — shows fill bar + "You're $X away"
 *   - unlocked: cart >= threshold — full bar + success message
 */

'use client';

import { formatPrice, type FormatPriceOptions } from '@template-project/afrodita-components';
import { useTranslation } from '@/lib/i18n/client';
import {
  ProgressBarContainer,
  ProgressBarTrack,
  ProgressBarFill,
  ProgressBarLabels,
  ProgressBarLabel,
  ProgressBarMessage,
  ProgressBarMessageBold,
} from './CartSummaryV2.styled';
import type { FreeShippingState } from '@/lib/hooks/use-free-shipping';

export interface FreeShippingProgressBarProps {
  freeShipping: FreeShippingState;
  priceOptions: FormatPriceOptions;
}

export function FreeShippingProgressBar({
  freeShipping,
  priceOptions,
}: Readonly<FreeShippingProgressBarProps>) {
  const { t } = useTranslation('sidecart');

  if (!freeShipping.isEnabled) {
    return null;
  }

  const currentFormatted = formatPrice(
    freeShipping.threshold - freeShipping.remaining,
    priceOptions,
  );
  const thresholdFormatted = formatPrice(freeShipping.threshold, priceOptions);
  const remainingFormatted = formatPrice(freeShipping.remaining, priceOptions);

  if (freeShipping.isUnlocked) {
    return (
      <ProgressBarContainer data-testid="free-shipping-bar">
        <ProgressBarTrack>
          <ProgressBarFill $percent={100} $unlocked />
        </ProgressBarTrack>
        <ProgressBarLabels>
          <ProgressBarLabel>{currentFormatted}</ProgressBarLabel>
          <ProgressBarLabel>{thresholdFormatted}</ProgressBarLabel>
        </ProgressBarLabels>
        <ProgressBarMessage
          dangerouslySetInnerHTML={{ __html: t('cart.freeShipping.unlocked') }}
          data-testid="free-shipping-unlocked"
        />
      </ProgressBarContainer>
    );
  }

  return (
    <ProgressBarContainer data-testid="free-shipping-bar">
      <ProgressBarTrack>
        <ProgressBarFill $percent={freeShipping.percentage} />
      </ProgressBarTrack>
      <ProgressBarLabels>
        <ProgressBarLabel>{currentFormatted}</ProgressBarLabel>
        <ProgressBarLabel>{thresholdFormatted}</ProgressBarLabel>
      </ProgressBarLabels>
      <ProgressBarMessage data-testid="free-shipping-progress">
        {t('cart.freeShipping.progressPrefix')}{' '}
        <ProgressBarMessageBold>{remainingFormatted}</ProgressBarMessageBold>{' '}
        {t('cart.freeShipping.progressSuffix')}{' '}
        <ProgressBarMessageBold>{t('cart.freeShipping.progressLabel')}</ProgressBarMessageBold>
      </ProgressBarMessage>
    </ProgressBarContainer>
  );
}
